import { SessionState, Slot } from './types';

/** Normalize presenter slot timestamps for API / DynamoDB (ISO strings). */
export const serializePresenterTime = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
};

export const slotToPresenter = (slot: Slot): Presenter => ({
  id: slot.id,
  name: slot.name,
  description: slot.description,
  flight: slot.flight,
  priority: slot.priority,
  slotType: slot.slotType,
  initialDuration: slot.initialDuration,
  currentDuration: slot.currentDuration,
  elapsedTime: slot.elapsedTime,
  startTime: slot.startTime,
  endTime: slot.endTime,
  dbElapsedTime: slot.dbElapsedTime,
});

export const presenterToSlot = (p: Presenter): Slot => ({
  id: p.id,
  name: p.name,
  description: p.description,
  flight: p.flight,
  priority: p.priority,
  slotType: p.slotType,
  initialDuration: p.initialDuration,
  currentDuration: p.currentDuration,
  elapsedTime: p.elapsedTime,
  startTime: serializePresenterTime(p.startTime),
  endTime: serializePresenterTime(p.endTime),
  dbElapsedTime: p.dbElapsedTime,
});

export const applySessionToAppState = (
  state: SessionState,
  setters: {
    setMeetingName: (n: string) => void;
    setMeetingLength: (n: number) => void;
    setPresenters: (p: Presenter[]) => void;
    setBonusTime: (b: Presenter) => void;
    setCursor: (c: number) => void;
    setPrevCursor: (c: number) => void;
    setPresenterOrder: (o: string[]) => void;
    setIsDebugModeEnabled: (d: boolean) => void;
    setIsDisplayTimeUnitSeconds: (d: boolean) => void;
    setMeetingStartTime: (t: any) => void;
    setMeetingEndTime: (t: any) => void;
  },
  currentCursor?: number
): void => {
  setters.setMeetingName(state.name);
  setters.setMeetingLength(state.meetingLength);
  setters.setPresenterOrder([...state.presenterOrder]);
  setters.setPresenters(state.slots.map(slotToPresenter));
  setters.setBonusTime(slotToPresenter(state.bonus));
  setters.setPrevCursor(currentCursor ?? state.cursor);
  setters.setCursor(state.cursor);
  setters.setIsDebugModeEnabled(state.debug);
  setters.setIsDisplayTimeUnitSeconds(state.displaySeconds ?? true);
  setters.setMeetingStartTime(state.meetingStartTime ?? '');
  setters.setMeetingEndTime(state.meetingEndTime ?? '');
};

export const buildSessionPatchFromUpdate = (
  values: StateUpdateObject,
  ctx: {
    presenters: Presenter[];
    bonusTime: Presenter;
    cursor: number;
    meetingLength: number;
    presenterOrder: string[];
  }
): Partial<SessionState> => {
  const patch: Partial<SessionState> = {};

  if (values.meetingLength !== undefined) patch.meetingLength = values.meetingLength;
  if (values.cursor !== undefined) patch.cursor = values.cursor;
  if (values.debugStatus !== undefined) patch.debug = values.debugStatus;
  if (values.displayTimeUnit !== undefined) patch.displaySeconds = values.displayTimeUnit;
  if (values.presenterOrder !== undefined) patch.presenterOrder = values.presenterOrder;

  if (values.meetingStartTime !== undefined) {
    patch.meetingStartTime =
      values.meetingStartTime instanceof Date
        ? values.meetingStartTime.toISOString()
        : String(values.meetingStartTime);
  }
  if (values.meetingEndTime !== undefined) {
    patch.meetingEndTime =
      values.meetingEndTime instanceof Date
        ? values.meetingEndTime.toISOString()
        : String(values.meetingEndTime);
  }

  if (values.presentersUpdateAll !== undefined) {
    patch.slots = values.presentersUpdateAll.map(presenterToSlot);
  } else if (values.presenterUpdate) {
    const slots = ctx.presenters.map(presenterToSlot);
    for (const p of values.presenterUpdate) {
      const idx = slots.findIndex((s) => s.id === p.id);
      if (idx >= 0) slots[idx] = presenterToSlot(p);
    }
    patch.slots = slots;
  }

  if (values.bonusTime !== undefined) {
    patch.bonus = presenterToSlot(values.bonusTime);
  }

  if (values.presenterAdd?.length) {
    const slots = (patch.slots ?? ctx.presenters.map(presenterToSlot)).slice();
    const order = (patch.presenterOrder ?? ctx.presenterOrder).slice();
    for (const p of values.presenterAdd) {
      if (!slots.some((s) => s.id === p.id)) {
        slots.push(presenterToSlot(p));
        order.push(p.id);
      }
    }
    patch.slots = slots;
    patch.presenterOrder = order;
  }

  if (values.presenterDelete?.length) {
    const removeIds = new Set(values.presenterDelete.map((p) => p.id));
    patch.slots = (patch.slots ?? ctx.presenters.map(presenterToSlot)).filter((s) => !removeIds.has(s.id));
    patch.presenterOrder = (patch.presenterOrder ?? ctx.presenterOrder).filter((id) => !removeIds.has(id));
  }

  return patch;
};
